/**
 * ChildManagement.js - Admin Student Management
 * 
 * Allows admins to:
 * - View all enrolled students in table format
 * - Add new students with age/room validation
 * - Edit student information
 * - Bulk upload via CSV
 * - Filter by room, status, scheduled day
 * - Search by name
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

// Day codes for scheduled days
const DAY_OPTIONS = [
  { code: 'M', label: 'Mon' },
  { code: 'T', label: 'Tue' },
  { code: 'W', label: 'Wed' },
  { code: 'TH', label: 'Thu' },
  { code: 'F', label: 'Fri' }
];

// Status options
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'graduated', label: 'Graduated' }
];

function ChildManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  const fileInputRef = useRef(null);
  
  // Data state
  const [children, setChildren] = useState([]);
  const [rooms, setRooms] = useState([]);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterDay, setFilterDay] = useState('');
  
  // Sort state - array of { field, direction } for multi-column sorting
  const [sortConfig, setSortConfig] = useState([
    { field: 'lastName', direction: 'asc' }
  ]);  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    enrollmentDate: '',
    primaryRoomId: '',
    scheduledDays: ['M', 'T', 'W', 'TH', 'F'],
    useCustomHours: false,
    customHoursStart: '08:00',
    customHoursEnd: '17:00',
    status: 'active',
    notes: '',
    parentName: '',
    parentPhone: '',
    parentEmail: ''
  });
  
  // CSV upload state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  
  // UI state
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [ageWarning, setAgeWarning] = useState('');

  // Load data
  const loadData = () => {
    setChildren(dataStore.getChildren());
    setRooms(dataStore.getRooms());
  };

  useEffect(() => {
    loadData();
  }, []);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Filter children
  const filteredChildren = children.filter(child => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const fullName = `${child.firstName} ${child.lastName}`.toLowerCase();
      if (!fullName.includes(term)) return false;
    }
    if (filterRoom && child.primaryRoomId !== filterRoom) return false;
    if (filterStatus && child.status !== filterStatus) return false;
    if (filterDay && !child.scheduledDays.includes(filterDay)) return false;
    return true;
  });

  // Sort children based on sortConfig (multi-column)
  const sortedChildren = [...filteredChildren].sort((a, b) => {
    for (const { field, direction } of sortConfig) {
      let aVal, bVal;
      
      // Get values based on field
      switch (field) {
        case 'name':
        case 'lastName':
          aVal = `${a.lastName} ${a.firstName}`.toLowerCase();
          bVal = `${b.lastName} ${b.firstName}`.toLowerCase();
          break;
        case 'firstName':
          aVal = a.firstName.toLowerCase();
          bVal = b.firstName.toLowerCase();
          break;
        case 'room':
          const roomA = rooms.find(r => r.id === a.primaryRoomId);
          const roomB = rooms.find(r => r.id === b.primaryRoomId);
          aVal = (roomA?.shortName || 'Unknown').toLowerCase();
          bVal = (roomB?.shortName || 'Unknown').toLowerCase();
          break;
        case 'age':
          const ageA = dataStore.calculateChildAge(a.dateOfBirth);
          const ageB = dataStore.calculateChildAge(b.dateOfBirth);
          aVal = ageA ? ageA.months : 0;
          bVal = ageB ? ageB.months : 0;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        default:
          aVal = a[field] || '';
          bVal = b[field] || '';
      }
      
      // Compare
      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      
      if (comparison !== 0) {
        return direction === 'asc' ? comparison : -comparison;
      }
    }
    return 0;
  });

  // Handle column header click for sorting
  const handleSort = (field) => {
    setSortConfig(prev => {
      const existingIndex = prev.findIndex(s => s.field === field);
      
      if (existingIndex === -1) {
        // Field not in sort - add it as primary sort (shift others down)
        return [{ field, direction: 'asc' }, ...prev.slice(0, 2)];
      } else if (existingIndex === 0) {
        // Already primary sort - toggle direction or remove
        if (prev[0].direction === 'asc') {
          return [{ field, direction: 'desc' }, ...prev.slice(1)];
        } else {
          // Remove this sort if it was desc (cycle: asc -> desc -> remove)
          return prev.length > 1 ? prev.slice(1) : [{ field: 'lastName', direction: 'asc' }];
        }
      } else {
        // Field exists but not primary - promote it to primary
        const newConfig = prev.filter(s => s.field !== field);
        return [{ field, direction: 'asc' }, ...newConfig.slice(0, 2)];
      }
    });
  };

  // Get sort indicator for a column
  const getSortIndicator = (field) => {
    const sortIndex = sortConfig.findIndex(s => s.field === field);
    if (sortIndex === -1) return '';
    const { direction } = sortConfig[sortIndex];
    const arrow = direction === 'asc' ? '▲' : '▼';
    return ` ${arrow}`;
  };

  // Get room name by ID
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.shortName : 'Unknown';
  };

  // Validate age against room's age range
  const validateAgeForRoom = (dob, roomId) => {
    if (!dob || !roomId) return { valid: true, warning: '' };
    
    const room = rooms.find(r => r.id === roomId);
    if (!room || !room.ageRange) return { valid: true, warning: '' };
    
    const age = dataStore.calculateChildAge(dob);
    if (!age) return { valid: true, warning: '' };
    
    const ageInMonths = age.months;
    const { minMonths, maxMonths, displayText } = room.ageRange;
    
    if (ageInMonths < minMonths) {
      return { 
        valid: false, 
        warning: `Warning: Student is ${age.displayText} old, but ${room.name} is for ages ${displayText}. Student may be too young.`
      };
    }
    if (ageInMonths > maxMonths) {
      return { 
        valid: false, 
        warning: `Warning: Student is ${age.displayText} old, but ${room.name} is for ages ${displayText}. Student may be too old.`
      };
    }
    
    return { valid: true, warning: '' };
  };

  // Handle DOB or Room change - check age validation
  useEffect(() => {
    if (formData.dateOfBirth && formData.primaryRoomId) {
      const validation = validateAgeForRoom(formData.dateOfBirth, formData.primaryRoomId);
      setAgeWarning(validation.warning);
    } else {
      setAgeWarning('');
    }
  }, [formData.dateOfBirth, formData.primaryRoomId]);

  // Open modal for new child
  const handleAddNew = () => {
    setEditingChild(null);
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      enrollmentDate: new Date().toISOString().split('T')[0],
      primaryRoomId: rooms.length > 0 ? rooms[0].id : '',
      scheduledDays: ['M', 'T', 'W', 'TH', 'F'],
      useCustomHours: false,
      customHoursStart: '08:00',
      customHoursEnd: '17:00',
      status: 'active',
      notes: '',
      parentName: '',
      parentPhone: '',
      parentEmail: ''
    });
    setAgeWarning('');
    setShowModal(true);
    setError('');
  };

  // Open modal for editing
  const handleEdit = (child) => {
    setEditingChild(child);
    setFormData({
      firstName: child.firstName,
      lastName: child.lastName,
      dateOfBirth: child.dateOfBirth,
      enrollmentDate: child.enrollmentDate || '',
      primaryRoomId: child.primaryRoomId,
      scheduledDays: child.scheduledDays || ['M', 'T', 'W', 'TH', 'F'],
      useCustomHours: child.useCustomHours || !!child.customHours,
      customHoursStart: child.customHoursStart || child.customHours?.start || '08:00',
      customHoursEnd: child.customHoursEnd || child.customHours?.end || '17:00',
      status: child.status,
      notes: child.notes || '',
      parentName: child.parentName || child.parentContact?.name || '',
      parentPhone: child.parentPhone || child.parentContact?.phone || '',
      parentEmail: child.parentEmail || child.parentContact?.email || ''
    });
    setShowModal(true);
    setError('');
  };

  // Toggle scheduled day
  const toggleDay = (dayCode) => {
    setFormData(prev => {
      const days = prev.scheduledDays.includes(dayCode)
        ? prev.scheduledDays.filter(d => d !== dayCode)
        : [...prev.scheduledDays, dayCode];
      return { ...prev, scheduledDays: days };
    });
  };

  // Save child (create or update)
  const handleSave = async () => {
    setError('');
    if (!formData.firstName.trim()) { setError('First name is required'); return; }
    if (!formData.lastName.trim()) { setError('Last name is required'); return; }
    if (!formData.dateOfBirth) { setError('Date of birth is required'); return; }
    if (!formData.primaryRoomId) { setError('Primary room is required'); return; }
    if (formData.scheduledDays.length === 0) { setError('At least one scheduled day is required'); return; }
    
    const dob = new Date(formData.dateOfBirth + 'T12:00:00');
    if (dob > new Date()) { setError('Date of birth cannot be in the future'); return; }

    // Age warning is just a warning, not a blocker
    try {
      const childData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth,
        enrollmentDate: formData.enrollmentDate || new Date().toISOString().split('T')[0],
        primaryRoomId: formData.primaryRoomId,
        scheduledDays: formData.scheduledDays,
        useCustomHours: formData.useCustomHours,
        customHoursStart: formData.useCustomHours ? formData.customHoursStart : '08:00',
        customHoursEnd: formData.useCustomHours ? formData.customHoursEnd : '17:00',
        status: formData.status,
        notes: formData.notes.trim(),
        parentContact: { name: formData.parentName.trim(), phone: formData.parentPhone.trim(), email: formData.parentEmail.trim() }
      };

      if (editingChild) {
        await dataStore.updateChild(editingChild.id, childData, currentUser);
        setMessage(`${childData.firstName} ${childData.lastName} updated successfully!`);
      } else {
        await dataStore.addChild(childData, currentUser);
        setMessage(`${childData.firstName} ${childData.lastName} enrolled successfully!`);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete child
  const handleDelete = async (child) => {
    const action = child.status === 'active' ? 'deactivate' : 'permanently delete';
    if (window.confirm(`Are you sure you want to ${action} ${child.firstName} ${child.lastName}?`)) {
      try {
        const hardDelete = child.status !== 'active';
        await dataStore.deleteChild(child.id, hardDelete, currentUser);
        setMessage(`${child.firstName} ${child.lastName} ${hardDelete ? 'deleted' : 'deactivated'}`);
        loadData();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Download CSV template
  const downloadCsvTemplate = () => {
    const headers = ['FirstName', 'LastName', 'DateOfBirth', 'Room', 'ScheduledDays', 'CustomStartTime', 'CustomEndTime', 'Status', 'Notes', 'ParentName', 'ParentPhone', 'ParentEmail'];
    const exampleRow = ['John', 'Doe', '2023-06-15', 'Infant 1', 'M T W TH F', '8:00 AM', '5:00 PM', 'active', 'Allergic to peanuts', 'Jane Doe', '555-123-4567', 'jane@email.com'];
    
    const csvContent = [
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'student_upload_template.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseCsv(text);
    };
    reader.readAsText(file);
  };

  // Parse a CSV line handling quoted fields
  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Parse CSV text
  const parseCsv = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      setCsvErrors(['CSV file must have a header row and at least one data row']);
      return;
    }

    const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
    const requiredHeaders = ['firstname', 'lastname', 'dateofbirth'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      setCsvErrors([`Missing required columns: ${missingHeaders.join(', ')}. Required: FirstName, LastName, DateOfBirth`]);
      return;
    }

    // Helper to normalize date to YYYY-MM-DD format
    const normalizeDate = (dateStr) => {
      if (!dateStr) return '';
      // Try M/D/YYYY or MM/DD/YYYY format first
      const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        const month = slashMatch[1].padStart(2, '0');
        const day = slashMatch[2].padStart(2, '0');
        const year = slashMatch[3];
        return `${year}-${month}-${day}`;
      }
      // Already YYYY-MM-DD format
      const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (isoMatch) {
        const year = isoMatch[1];
        const month = isoMatch[2].padStart(2, '0');
        const day = isoMatch[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return dateStr; // Return as-is if unrecognized
    };

    const errors = [];
    const preview = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]);
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });

      // Normalize and validate date
      const normalizedDob = normalizeDate(row.dateofbirth);

      // Validate required fields
      if (!row.firstname) errors.push(`Row ${i + 1}: First name is required`);
      if (!row.lastname) errors.push(`Row ${i + 1}: Last name is required`);
      if (!row.dateofbirth) errors.push(`Row ${i + 1}: Date of birth is required`);
      
      // Validate date format
      if (normalizedDob && isNaN(Date.parse(normalizedDob))) {
        errors.push(`Row ${i + 1}: Invalid date format for DateOfBirth (use MM/DD/YYYY or YYYY-MM-DD)`);
      }

      // Find room by name or shortname
      let roomId = '';
      if (row.room || row.primaryroom) {
        const roomName = (row.room || row.primaryroom).toLowerCase();
        const room = rooms.find(r => 
          r.name.toLowerCase() === roomName || 
          r.shortName.toLowerCase() === roomName
        );
        if (room) {
          roomId = room.id;
        } else {
          errors.push(`Row ${i + 1}: Room "${row.room || row.primaryroom}" not found`);
        }
      }

      // Parse scheduled days
      let scheduledDays = ['M', 'T', 'W', 'TH', 'F'];
      if (row.scheduleddays || row.days) {
        const daysStr = (row.scheduleddays || row.days).toUpperCase();
        scheduledDays = daysStr.split(/[,\s]+/).filter(d => ['M', 'T', 'W', 'TH', 'F'].includes(d));
        if (scheduledDays.length === 0) scheduledDays = ['M', 'T', 'W', 'TH', 'F'];
      }

      // Parse custom hours (convert 12h to 24h format)
      let customHours = null;
      const startTimeStr = row.customstarttime || row.starttime || '';
      const endTimeStr = row.customendtime || row.endtime || '';
      if (startTimeStr && endTimeStr) {
        const parse12hTo24h = (timeStr) => {
          const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
          if (!match) return null;
          let hours = parseInt(match[1]);
          const minutes = match[2];
          const period = (match[3] || '').toUpperCase();
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return `${hours.toString().padStart(2, '0')}:${minutes}`;
        };
        const start24 = parse12hTo24h(startTimeStr);
        const end24 = parse12hTo24h(endTimeStr);
        if (start24 && end24) {
          customHours = { start: start24, end: end24 };
        }
      }

      preview.push({
        firstName: row.firstname,
        lastName: row.lastname,
        dateOfBirth: normalizedDob,
        primaryRoomId: roomId || (rooms.length > 0 ? rooms[0].id : ''),
        scheduledDays,
        customHours,
        status: (row.status || 'active').toLowerCase(),
        notes: row.notes || '',
        parentName: row.parentname || row.parent || '',
        parentPhone: row.parentphone || row.phone || '',
        parentEmail: row.parentemail || row.email || ''
      });
    }

    setCsvErrors(errors);
    setCsvPreview(preview);
    setShowCsvModal(true);
  };

  // Import CSV data
  const handleCsvImport = async () => {
    if (csvErrors.length > 0) {
      setError('Please fix CSV errors before importing');
      return;
    }

    let imported = 0;
    for (const data of csvPreview) {
      try {
        await dataStore.addChild({
          ...data,
          enrollmentDate: new Date().toISOString().split('T')[0],
          customHours: data.customHours || null,
          parentContact: { name: data.parentName, phone: data.parentPhone, email: data.parentEmail }
        }, currentUser);
        imported++;
      } catch (err) {
        console.error('Error importing child:', err);
      }
    }

    setShowCsvModal(false);
    setCsvData([]);
    setCsvPreview([]);
    setCsvErrors([]);
    loadData();
    setMessage(`Successfully imported ${imported} students!`);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // STYLES
  const containerStyle = { padding: '20px', maxWidth: '1400px', margin: '0 auto' };
  const headerCardStyle = { backgroundColor: theme.cardBackground, padding: '20px 25px', borderRadius: '8px', border: `1px solid ${theme.border}`, marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' };
  const titleStyle = { color: theme.text, margin: 0, fontSize: '1.5rem' };
  const buttonStyle = { padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.2s' };
  const primaryButtonStyle = { ...buttonStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff' };
  const secondaryButtonStyle = { ...buttonStyle, backgroundColor: colors.blue, color: darkMode ? '#1a1a2e' : '#fff' };
  const filterCardStyle = { backgroundColor: theme.cardBackground, padding: '20px', borderRadius: '8px', border: `1px solid ${theme.border}`, marginBottom: '20px' };
  const filterRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' };
  const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '5px' };
  const labelStyle = { fontSize: '0.8rem', color: theme.textSecondary, fontWeight: '500' };
  const inputStyle = { padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: '6px', backgroundColor: theme.background, color: theme.text, fontSize: '0.9rem', minWidth: '150px' };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  
  const tableContainerStyle = { backgroundColor: theme.cardBackground, borderRadius: '8px', border: `1px solid ${theme.border}`, overflow: 'auto' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', minWidth: '1050px' };
  const thStyle = { padding: '12px', backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600' };
  const thClickableStyle = { ...thStyle, cursor: 'pointer', userSelect: 'none', transition: 'background-color 0.2s' };
  const tdStyle = { padding: '12px', borderBottom: `1px solid ${theme.border}`, color: theme.text, verticalAlign: 'middle' };

  const statusBadgeStyle = (status) => {
    const statusColors = { active: { bg: colors.green, text: darkMode ? '#1a1a2e' : '#fff' }, inactive: { bg: colors.red, text: '#fff' }, graduated: { bg: colors.purple, text: '#fff' } };
    const style = statusColors[status] || statusColors.active;
    return { padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: style.bg, color: style.text, display: 'inline-block' };
  };

  const dayBadgeStyle = (isActive) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '600', marginRight: '2px',
    backgroundColor: isActive ? colors.blue : theme.backgroundSecondary, color: isActive ? (darkMode ? '#1a1a2e' : '#fff') : theme.textSecondary
  });

  const actionButtonStyle = { padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', marginRight: '6px' };
  const messageStyle = { padding: '12px 20px', borderRadius: '6px', marginBottom: '20px', textAlign: 'center', fontWeight: '500' };
  const warningStyle = { padding: '10px 16px', borderRadius: '6px', marginBottom: '16px', backgroundColor: colors.orange, color: '#000', fontSize: '0.9rem' };

  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { backgroundColor: theme.cardBackground, borderRadius: '12px', padding: '24px', maxWidth: '550px', width: '95%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' };
  const modalTitleStyle = { fontSize: '1.2rem', fontWeight: '600', color: theme.text, marginBottom: '20px', paddingBottom: '12px', borderBottom: `1px solid ${theme.border}` };
  const formGroupStyle = { marginBottom: '16px' };
  const formLabelStyle = { display: 'block', fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '6px', fontWeight: '500' };
  const formInputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: '6px', backgroundColor: theme.background, color: theme.text, fontSize: '0.9rem', boxSizing: 'border-box' };
  const formRowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
  const checkboxLabelStyle = { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: theme.text };
  const dayToggleStyle = { display: 'flex', gap: '8px', flexWrap: 'wrap' };
  const dayToggleButtonStyle = (isActive) => ({ padding: '8px 14px', border: `2px solid ${isActive ? colors.blue : theme.border}`, borderRadius: '6px', backgroundColor: isActive ? colors.blue : 'transparent', color: isActive ? (darkMode ? '#1a1a2e' : '#fff') : theme.text, cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.2s' });
  const modalButtonRowStyle = { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` };


  // RENDER
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerCardStyle}>
        <h1 style={titleStyle}>📋 Student Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          <button 
            style={{ ...secondaryButtonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text, border: `1px solid ${theme.border}` }} 
            onClick={downloadCsvTemplate}
          >
            📄 Download Template
          </button>
          <button style={secondaryButtonStyle} onClick={() => fileInputRef.current?.click()}>
            📁 Bulk Upload CSV
          </button>
          <button style={primaryButtonStyle} onClick={handleAddNew}>+ Enroll New Student</button>
        </div>
      </div>

      {/* Messages */}
      {message && <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#000' }}>{message}</div>}
      {error && !showModal && <div style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#000' }}>{error}</div>}

      {/* Filters */}
      <div style={filterCardStyle}>
        <div style={filterRowStyle}>
          <div style={{ ...filterGroupStyle, flex: 1, minWidth: '200px' }}>
            <label style={labelStyle}>Search</label>
            <input type="text" placeholder="Search by name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={inputStyle} />
          </div>
          <div style={filterGroupStyle}>
            <label style={labelStyle}>Room</label>
            <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} style={selectStyle}>
              <option value="">All Rooms</option>
              {rooms.map(room => <option key={room.id} value={room.id}>{room.name}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={labelStyle}>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={labelStyle}>Scheduled Day</label>
            <select value={filterDay} onChange={(e) => setFilterDay(e.target.value)} style={selectStyle}>
              <option value="">Any Day</option>
              {DAY_OPTIONS.map(opt => <option key={opt.code} value={opt.code}>{opt.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Results count and sort info */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <span style={{ color: theme.text, fontSize: '0.9rem', opacity: 0.8 }}>
          Showing {sortedChildren.length} of {children.length} students
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
          <span style={{ color: theme.textSecondary }}>Sort:</span>
          {sortConfig.map((s, idx) => (
            <span key={s.field} style={{ 
              padding: '4px 8px', 
              backgroundColor: theme.backgroundSecondary, 
              borderRadius: '4px', 
              color: theme.text,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {s.field === 'lastName' ? 'Name' : s.field.charAt(0).toUpperCase() + s.field.slice(1)} {s.direction === 'asc' ? '▲' : '▼'}
            </span>
          ))}
          {sortConfig.length > 0 && (
            <button 
              onClick={() => setSortConfig([{ field: 'lastName', direction: 'asc' }])}
              style={{ 
                padding: '4px 8px', 
                backgroundColor: 'transparent', 
                border: `1px solid ${theme.border}`, 
                borderRadius: '4px', 
                color: theme.textSecondary, 
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Students Table */}
      {sortedChildren.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary, backgroundColor: theme.cardBackground, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
          {children.length === 0 ? 'No students enrolled yet. Click "Enroll New Student" to add one.' : 'No students match the current filters.'}
        </div>
      ) : (
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thClickableStyle} onClick={() => handleSort('lastName')}>Name{getSortIndicator('lastName')}</th>
                <th style={thClickableStyle} onClick={() => handleSort('age')}>Age{getSortIndicator('age')}</th>
                <th style={thClickableStyle} onClick={() => handleSort('room')}>Room{getSortIndicator('room')}</th>
                <th style={thStyle}>Schedule</th>
                <th style={thStyle}>Hours</th>
                <th style={{ ...thStyle, minWidth: '150px' }}>Notes</th>
                <th style={thClickableStyle} onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedChildren.map((child) => {
                const age = dataStore.calculateChildAge(child.dateOfBirth);
                return (
                  <tr key={child.id}>
                    <td style={tdStyle}>
                      <strong>{child.firstName} {child.lastName}</strong>
                    </td>
                    <td style={tdStyle}>{age ? age.displayText : 'Unknown'}</td>
                    <td style={tdStyle}>🏫 {getRoomName(child.primaryRoomId)}</td>
                    <td style={tdStyle}>
                      {DAY_OPTIONS.map(day => <span key={day.code} style={dayBadgeStyle(child.scheduledDays?.includes(day.code))}>{day.code}</span>)}
                    </td>
                    <td style={tdStyle}>
                      {child.useCustomHours ? `${formatTime(child.customHoursStart)} - ${formatTime(child.customHoursEnd)}` : 'Default'}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: '200px', wordWrap: 'break-word', whiteSpace: 'normal', fontSize: '0.85rem', color: theme.textSecondary }}>
                      {child.notes || '-'}
                    </td>
                    <td style={tdStyle}>
                      <span style={statusBadgeStyle(child.status)}>{child.status}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button style={{ ...actionButtonStyle, backgroundColor: colors.blue, color: darkMode ? '#1a1a2e' : '#000' }} onClick={() => handleEdit(child)}>Edit</button>
                      <button style={{ ...actionButtonStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#000' }} onClick={() => handleDelete(child)}>{child.status === 'active' ? 'Deactivate' : 'Delete'}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>{editingChild ? '✏️ Edit Student' : '📋 Enroll New Student'}</h2>
            {error && <div style={{ ...messageStyle, backgroundColor: colors.red, color: '#fff', marginBottom: '16px' }}>{error}</div>}
            {ageWarning && <div style={warningStyle}>❗ {ageWarning}</div>}
            
            {/* Name Row */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>First Name *</label>
                <input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} style={formInputStyle} placeholder="First name" />
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Last Name *</label>
                <input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} style={formInputStyle} placeholder="Last name" />
              </div>
            </div>
            
            {/* DOB & Enrollment Row */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Date of Birth *</label>
                <input type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})} style={formInputStyle} />
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Enrollment Date</label>
                <input type="date" value={formData.enrollmentDate} onChange={(e) => setFormData({...formData, enrollmentDate: e.target.value})} style={formInputStyle} />
              </div>
            </div>
            
            {/* Room & Status Row */}
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Primary Room *</label>
                <select value={formData.primaryRoomId} onChange={(e) => setFormData({...formData, primaryRoomId: e.target.value})} style={{...formInputStyle, cursor: 'pointer'}}>
                  <option value="">Select a room</option>
                  {rooms.map(room => <option key={room.id} value={room.id}>{room.name} {room.ageRange ? `(${room.ageRange.displayText})` : ''}</option>)}
                </select>
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Status</label>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} style={{...formInputStyle, cursor: 'pointer'}}>
                  {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            </div>
            
            {/* Scheduled Days */}
            <div style={formGroupStyle}>
              <label style={formLabelStyle}>Scheduled Days *</label>
              <div style={dayToggleStyle}>
                {DAY_OPTIONS.map(day => (
                  <button key={day.code} type="button" style={dayToggleButtonStyle(formData.scheduledDays.includes(day.code))} onClick={() => toggleDay(day.code)}>{day.label}</button>
                ))}
              </div>
            </div>
            
            {/* Custom Hours */}
            <div style={formGroupStyle}>
              <label style={checkboxLabelStyle}>
                <input type="checkbox" checked={formData.useCustomHours} onChange={(e) => setFormData({...formData, useCustomHours: e.target.checked})} />
                Use custom hours (instead of room defaults)
              </label>
              {formData.useCustomHours && (
                <div style={{ ...formRowStyle, marginTop: '12px' }}>
                  <div>
                    <label style={formLabelStyle}>Start Time</label>
                    <input type="time" value={formData.customHoursStart} onChange={(e) => setFormData({...formData, customHoursStart: e.target.value})} style={formInputStyle} />
                  </div>
                  <div>
                    <label style={formLabelStyle}>End Time</label>
                    <input type="time" value={formData.customHoursEnd} onChange={(e) => setFormData({...formData, customHoursEnd: e.target.value})} style={formInputStyle} />
                  </div>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div style={formGroupStyle}>
              <label style={formLabelStyle}>Notes</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} style={{...formInputStyle, minHeight: '70px', resize: 'vertical'}} placeholder="Allergies, medical info, special needs..." />
            </div>
            
            {/* Parent Contact Section */}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
              <div style={{ ...formLabelStyle, fontSize: '0.95rem', marginBottom: '12px' }}>👨‍👩‍👧 Parent/Guardian Contact</div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Parent Name</label>
                <input type="text" value={formData.parentName} onChange={(e) => setFormData({...formData, parentName: e.target.value})} style={formInputStyle} placeholder="Parent/Guardian name" />
              </div>
              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={formLabelStyle}>Phone</label>
                  <input type="tel" value={formData.parentPhone} onChange={(e) => setFormData({...formData, parentPhone: e.target.value})} style={formInputStyle} placeholder="(555) 123-4567" />
                </div>
                <div style={formGroupStyle}>
                  <label style={formLabelStyle}>Email</label>
                  <input type="email" value={formData.parentEmail} onChange={(e) => setFormData({...formData, parentEmail: e.target.value})} style={formInputStyle} placeholder="parent@email.com" />
                </div>
              </div>
            </div>
            
            {/* Modal Buttons */}
            <div style={modalButtonRowStyle}>
              <button style={{ ...buttonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={primaryButtonStyle} onClick={handleSave}>{editingChild ? 'Save Changes' : 'Enroll Student'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview Modal */}
      {showCsvModal && (
        <div style={modalOverlayStyle} onClick={() => setShowCsvModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>📁 CSV Upload Preview</h2>
            
            {csvErrors.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.orange, color: '#fff', borderRadius: '6px' }}>
                <strong>⚠️ Some rows have issues (will be skipped):</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  {csvErrors.slice(0, 5).map((err, idx) => <li key={idx}>{err}</li>)}
                  {csvErrors.length > 5 && <li>...and {csvErrors.length - 5} more issues</li>}
                </ul>
              </div>
            )}
            
            {csvPreview.length > 0 ? (
              <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: colors.green, fontWeight: 'bold' }}>
                ✅ {csvPreview.length} valid student{csvPreview.length !== 1 ? 's' : ''} ready to import:
              </div>
            ) : (
              <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: colors.red, fontWeight: 'bold' }}>
                ❌ No valid students found. Please fix the errors above.
              </div>
            )}
            
            <div style={{ maxHeight: '300px', overflow: 'auto', border: `1px solid ${theme.border}`, borderRadius: '6px' }}>
              <table style={{ ...tableStyle, fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, padding: '8px' }}>Name</th>
                    <th style={{ ...thStyle, padding: '8px' }}>DOB</th>
                    <th style={{ ...thStyle, padding: '8px' }}>Room</th>
                    <th style={{ ...thStyle, padding: '8px' }}>Days</th>
                    <th style={{ ...thStyle, padding: '8px' }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.slice(0, 10).map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ ...tdStyle, padding: '8px' }}>{row.firstName} {row.lastName}</td>
                      <td style={{ ...tdStyle, padding: '8px' }}>{row.dateOfBirth}</td>
                      <td style={{ ...tdStyle, padding: '8px' }}>{getRoomName(row.primaryRoomId)}</td>
                      <td style={{ ...tdStyle, padding: '8px' }}>{row.scheduledDays.join(', ')}</td>
                      <td style={{ ...tdStyle, padding: '8px' }}>{row.customHours ? `${formatTime(row.customHours.start)}-${formatTime(row.customHours.end)}` : 'Default'}</td>
                    </tr>
                  ))}
                  {csvPreview.length > 10 && (
                    <tr>
                      <td colSpan={5} style={{ ...tdStyle, padding: '8px', textAlign: 'center', color: theme.textSecondary }}>
                        ...and {csvPreview.length - 10} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div style={{ marginTop: '12px', fontSize: '0.8rem', color: theme.textSecondary }}>
              <strong>Expected CSV columns:</strong> FirstName, LastName, DateOfBirth (required), Room, ScheduledDays, CustomStartTime, CustomEndTime, Status, Notes, ParentName, ParentPhone, ParentEmail
            </div>
            
            <div style={modalButtonRowStyle}>
              <button style={{ ...buttonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text }} onClick={() => { setShowCsvModal(false); setCsvPreview([]); setCsvErrors([]); if(fileInputRef.current) fileInputRef.current.value = ''; }}>Cancel</button>
              <button style={primaryButtonStyle} onClick={handleCsvImport} disabled={csvPreview.length === 0}>
                Import {csvPreview.length} Student{csvPreview.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChildManagement;
